/**
 * A surface drawn in the organization's brand (ORDR-139): the Ordering App and
 * the Organization admin, and NOTHING ELSE. The kitchen and staff surfaces (the
 * Order Manager) never render inside this; `kitchenUnbranded.test.tsx` holds
 * that line.
 *
 * The framework's `BrandProvider` does the colours: it resolves the tier
 * (dropping any colour that is not strict `#RRGGBB`, a second guard after
 * `brandApi.ts`) and sets the brand's custom properties inline on its scope,
 * where they win over the neutral default in `neutral.css`. While the brand is
 * loading or absent it is handed no tier, sets nothing, and the surface is
 * neutral. It stays MOUNTED across that change, so the brand arriving restyles
 * the surface rather than remounting it.
 *
 * This file adds the three places the brand shows outside the colours: the
 * header (name and logo), the favicon and the browser's theme colour. The
 * document `<title>` is the shell's (`ModeTitle`), which reads `useBrand()`.
 *
 * NO PWA MANIFEST IS BRANDED because the app serves none (measured 2026-10-04:
 * no `public/`, no `<link rel="manifest">`, no manifest plugin).
 */

import { useEffect, type ReactElement, type ReactNode } from "react";
import { BrandProvider, Logo, useBrand } from "@pirouz-ca/ui-components";
import { useOrganizationBrand } from "./organizationBrand.js";

export interface BrandedSurfaceProps {
  children: ReactNode;
}

/** Point the head element `selector` names at `value` while mounted; put it back after. */
function useHeadAttribute(selector: string, create: () => HTMLElement, attribute: string, value: string | undefined): void {
  useEffect(() => {
    if (value === undefined) {
      return undefined;
    }
    let element = document.head.querySelector<HTMLElement>(selector);
    const created = element === null;
    if (element === null) {
      element = create();
      document.head.appendChild(element);
    }
    const previous = element.getAttribute(attribute);
    element.setAttribute(attribute, value);
    const target = element;
    return () => {
      if (created) {
        target.remove();
      } else if (previous === null) {
        target.removeAttribute(attribute);
      } else {
        target.setAttribute(attribute, previous);
      }
    };
  }, [selector, create, attribute, value]);
}

function iconLink(): HTMLElement {
  const link = document.createElement("link");
  link.rel = "icon";
  return link;
}

function themeColorMeta(): HTMLElement {
  const meta = document.createElement("meta");
  meta.name = "theme-color";
  return meta;
}

function BrandHeader(): ReactElement | null {
  const { displayName, logoUrl, faviconUrl, themeColor } = useBrand();
  useHeadAttribute("link[rel~='icon']", iconLink, "href", faviconUrl);
  useHeadAttribute("meta[name='theme-color']", themeColorMeta, "content", themeColor);

  if (displayName === undefined && logoUrl === undefined) {
    return null;
  }
  return (
    // Drawn as the canvases' header (ORDR-164): the name at 22px in the brand's
    // primary colour over a hairline rule.
    <header
      data-testid="brand-header"
      className="flex items-center gap-2.5 border-b border-[var(--color-neutral-200)] px-5 py-4"
    >
      {/* Decorative beside the name; a logo-only brand has no words to give it. */}
      {logoUrl !== undefined && <Logo logoUrl={logoUrl} alt="" size="sm" />}
      {displayName !== undefined && (
        <span
          data-testid="brand-name"
          className="text-[22px] font-bold tracking-[0.04em]"
          style={{ color: "var(--color-primary-700)" }}
        >
          {displayName}
        </span>
      )}
    </header>
  );
}

const NO_TIERS = [] as const;

export function BrandedSurface({ children }: BrandedSurfaceProps): ReactElement {
  const tier = useOrganizationBrand();
  return (
    <BrandProvider tiers={tier === null ? NO_TIERS : [tier]}>
      <BrandHeader />
      {children}
    </BrandProvider>
  );
}
